const DEFAULT_STYLE = Object.freeze({
  upColor: '#2962FF',
  downColor: '#F23645',
  upFillColor: '#2962ff2e',
  downFillColor: '#f236452e',
})

function drawArrow(ctx, startX, startY, endX, endY, arrowSize) {
  const deltaX = endX - startX
  const deltaY = endY - startY
  const length = Math.hypot(deltaX, deltaY)
  if (!length) return
  const unitX = deltaX / length
  const unitY = deltaY / length
  const wing = arrowSize * 0.6

  ctx.beginPath()
  ctx.moveTo(startX, startY)
  ctx.lineTo(endX, endY)
  ctx.moveTo(endX, endY)
  ctx.lineTo(
    endX - unitX * arrowSize + unitY * wing,
    endY - unitY * arrowSize - unitX * wing,
  )
  ctx.moveTo(endX, endY)
  ctx.lineTo(
    endX - unitX * arrowSize - unitY * wing,
    endY - unitY * arrowSize + unitX * wing,
  )
  ctx.stroke()
}

function finiteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value)
}

function timeToEpochSeconds(time) {
  if (finiteNumber(time)) return time
  if (typeof time === 'string') {
    const parsed = Date.parse(`${time}T00:00:00Z`)
    return Number.isFinite(parsed) ? parsed / 1000 : null
  }
  if (
    time &&
    Number.isInteger(time.year) &&
    Number.isInteger(time.month) &&
    Number.isInteger(time.day)
  ) {
    return Date.UTC(time.year, time.month - 1, time.day) / 1000
  }
  return null
}

function decimalPlaces(minMove) {
  if (!finiteNumber(minMove) || minMove <= 0) return 2
  const normalized = minMove.toFixed(10).replace(/0+$/, '')
  const dot = normalized.indexOf('.')
  return dot === -1 ? 0 : normalized.length - dot - 1
}

function signedNumber(value, digits) {
  if (!finiteNumber(value)) return '—'
  const normalized = Math.abs(value) < (0.5 * (10 ** -digits)) ? 0 : value
  const prefix = normalized > 0 ? '+' : ''
  return `${prefix}${normalized.toFixed(digits)}`
}

function signedInteger(value) {
  if (!finiteNumber(value)) return '—'
  const normalized = Math.round(value)
  const prefix = normalized > 0 ? '+' : ''
  const digits = Math.abs(normalized).toLocaleString('en-US')
  return `${normalized < 0 ? '-' : prefix}${digits}`
}

function formatElapsed(seconds) {
  if (!finiteNumber(seconds)) return null
  let remaining = Math.max(0, Math.round(seconds))
  const days = Math.floor(remaining / 86_400)
  remaining -= days * 86_400
  const hours = Math.floor(remaining / 3_600)
  remaining -= hours * 3_600
  const minutes = Math.floor(remaining / 60)
  const parts = []
  if (days) parts.push(`${days}d`)
  if (hours && parts.length < 2) parts.push(`${hours}h`)
  if (minutes && parts.length < 2) parts.push(`${minutes}m`)
  if (!parts.length) parts.push(`${remaining}s`)
  return parts.join(' ')
}

function minMoveFromSeries(series) {
  try {
    const minMove = Number(series?.options?.()?.priceFormat?.minMove)
    if (finiteNumber(minMove) && minMove > 0) return minMove
  } catch { /* use the equity-safe fallback */ }
  return 0.01
}

function measurePoint(param, series) {
  if (!finiteNumber(param?.logical) || !finiteNumber(param?.point?.y)) return null
  if (param?.paneIndex != null && param.paneIndex !== 0) return null
  const price = Number(series?.coordinateToPrice?.(param.point.y))
  if (!finiteNumber(price)) return null
  return {
    logical: param.logical,
    time: param.time ?? null,
    price,
  }
}

export function buildQuickMeasureStats({ start, end, minMove = 0.01 }) {
  if (!start || !end || !finiteNumber(start.price) || !finiteNumber(end.price)) return null
  const priceChange = end.price - start.price
  const percentChange = start.price === 0 ? null : (priceChange / start.price) * 100
  const normalizedMinMove = finiteNumber(minMove) && minMove > 0 ? minMove : 0.01
  const ticks = priceChange / normalizedMinMove
  const digits = decimalPlaces(normalizedMinMove)
  const bars = finiteNumber(start.logical) && finiteNumber(end.logical)
    ? Math.abs(Math.round(end.logical) - Math.round(start.logical))
    : null
  const startSeconds = timeToEpochSeconds(start.time)
  const endSeconds = timeToEpochSeconds(end.time)
  const elapsed = startSeconds == null || endSeconds == null
    ? null
    : formatElapsed(Math.abs(endSeconds - startSeconds))
  const barLabel = bars == null ? null : `${bars} ${bars === 1 ? 'bar' : 'bars'}`

  return {
    direction: priceChange < 0 ? 'down' : 'up',
    headline: `${signedNumber(priceChange, digits)} (${signedNumber(percentChange, 2)}%) ${signedInteger(ticks)}`,
    detail: [barLabel, elapsed].filter(Boolean).join(', '),
  }
}

export class QuickMeasurePrimitive {
  constructor({ onChange = null, eventTarget = null, style = null } = {}) {
    this.__isQuickMeasurePrimitive = true
    this._chart = null
    this._series = null
    this._requestUpdate = null
    this._onChange = typeof onChange === 'function' ? onChange : null
    this._eventTarget = eventTarget || (typeof window !== 'undefined' ? window : null)
    this._style = { ...DEFAULT_STYLE, ...(style || {}) }
    this._measurement = null
    this._paneViews = [new QuickMeasurePaneView(this)]
    this._handleClick = this._handleClick.bind(this)
    this._handleCrosshairMove = this._handleCrosshairMove.bind(this)
    this._handleKeyDown = this._handleKeyDown.bind(this)
  }

  attached(param) {
    this._chart = param.chart
    this._series = param.series
    this._requestUpdate = param.requestUpdate
    this._chart?.subscribeClick?.(this._handleClick)
    this._chart?.subscribeCrosshairMove?.(this._handleCrosshairMove)
    this._eventTarget?.addEventListener?.('keydown', this._handleKeyDown)
  }

  detached() {
    this._chart?.unsubscribeClick?.(this._handleClick)
    this._chart?.unsubscribeCrosshairMove?.(this._handleCrosshairMove)
    this._eventTarget?.removeEventListener?.('keydown', this._handleKeyDown)
    this._chart = null
    this._series = null
    this._requestUpdate = null
    this._measurement = null
  }

  snapshot() {
    if (!this._measurement) return null
    return {
      phase: this._measurement.phase,
      start: { ...this._measurement.start },
      end: { ...this._measurement.end },
      stats: { ...this._measurement.stats },
    }
  }

  clear() {
    if (!this._measurement) return
    this._measurement = null
    this._publish()
  }

  applyOptions(style = {}) {
    this._style = { ...this._style, ...style }
    if (typeof this._requestUpdate === 'function') this._requestUpdate()
  }

  updateAllViews() { /* renderer projects the current anchors */ }

  paneViews() {
    return this._paneViews
  }

  _handleClick(param) {
    const shiftKey = param?.sourceEvent?.shiftKey === true
    if (!shiftKey) {
      this.clear()
      return
    }

    const nextPoint = measurePoint(param, this._series)
    if (!nextPoint) return
    if (!this._measurement || this._measurement.phase === 'complete') {
      this._setMeasurement('measuring', nextPoint, nextPoint)
      return
    }
    this._setMeasurement('complete', this._measurement.start, nextPoint)
  }

  _handleCrosshairMove(param) {
    if (this._measurement?.phase !== 'measuring') return
    const nextPoint = measurePoint(param, this._series)
    if (!nextPoint) return
    this._setMeasurement('measuring', this._measurement.start, nextPoint)
  }

  _handleKeyDown(event) {
    if (event?.key === 'Escape') this.clear()
  }

  _setMeasurement(phase, start, end) {
    const stats = buildQuickMeasureStats({
      start,
      end,
      minMove: minMoveFromSeries(this._series),
    })
    if (!stats) return
    this._measurement = { phase, start, end, stats }
    this._publish()
  }

  _publish() {
    if (typeof this._requestUpdate === 'function') this._requestUpdate()
    if (this._onChange) this._onChange(this.snapshot())
  }
}

class QuickMeasurePaneView {
  constructor(primitive) {
    this._renderer = new QuickMeasurePaneRenderer(primitive)
  }

  zOrder() { return 'top' }

  renderer() { return this._renderer }
}

class QuickMeasurePaneRenderer {
  constructor(primitive) {
    this._primitive = primitive
  }

  draw(target) {
    const measurement = this._primitive._measurement
    const chart = this._primitive._chart
    const series = this._primitive._series
    if (!measurement || !chart || !series) return

    target.useBitmapCoordinateSpace((scope) => {
      const timeScale = chart.timeScale?.()
      const x1 = timeScale?.logicalToCoordinate?.(measurement.start.logical)
      const x2 = timeScale?.logicalToCoordinate?.(measurement.end.logical)
      const y1 = series.priceToCoordinate?.(measurement.start.price)
      const y2 = series.priceToCoordinate?.(measurement.end.price)
      if (![x1, x2, y1, y2].every(finiteNumber)) return

      const horizontalRatio = scope.horizontalPixelRatio || 1
      const verticalRatio = scope.verticalPixelRatio || 1
      const left = Math.min(x1, x2) * horizontalRatio
      const right = Math.max(x1, x2) * horizontalRatio
      const top = Math.min(y1, y2) * verticalRatio
      const bottom = Math.max(y1, y2) * verticalRatio
      const width = Math.max(1, right - left)
      const height = Math.max(1, bottom - top)
      const color = measurement.stats.direction === 'down'
        ? this._primitive._style.downColor
        : this._primitive._style.upColor
      const fill = measurement.stats.direction === 'down'
        ? this._primitive._style.downFillColor
        : this._primitive._style.upFillColor
      const ctx = scope.context

      ctx.save()
      ctx.fillStyle = fill
      ctx.fillRect(left, top, width, height)
      ctx.strokeStyle = color
      ctx.lineWidth = Math.max(1, horizontalRatio)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      const arrowSize = 5 * Math.max(horizontalRatio, verticalRatio)
      drawArrow(
        ctx,
        (left + right) / 2,
        y1 * verticalRatio,
        (left + right) / 2,
        y2 * verticalRatio,
        arrowSize,
      )
      drawArrow(
        ctx,
        x1 * horizontalRatio,
        (top + bottom) / 2,
        x2 * horizontalRatio,
        (top + bottom) / 2,
        arrowSize,
      )

      const fontSize = Math.max(10, Math.round(11 * verticalRatio))
      const lineHeight = Math.round(fontSize * 1.35)
      ctx.font = `500 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`
      const labelWidth = Math.max(
        ctx.measureText(measurement.stats.headline).width,
        ctx.measureText(measurement.stats.detail).width,
      ) + (16 * horizontalRatio)
      const labelHeight = (lineHeight * 2) + (8 * verticalRatio)
      const labelGap = 4 * verticalRatio
      const canvasWidth = scope.bitmapSize?.width || ctx.canvas?.width || right
      const canvasHeight = scope.bitmapSize?.height || ctx.canvas?.height || bottom
      const centerX = Math.min(
        canvasWidth - labelWidth / 2,
        Math.max(labelWidth / 2, (left + right) / 2),
      )
      const preferredY = measurement.end.price <= measurement.start.price
        ? bottom + labelGap + labelHeight / 2
        : top - labelGap - labelHeight / 2
      const centerY = Math.min(
        canvasHeight - labelHeight / 2,
        Math.max(labelHeight / 2, preferredY),
      )

      ctx.fillStyle = color
      ctx.fillRect(centerX - labelWidth / 2, centerY - labelHeight / 2, labelWidth, labelHeight)
      ctx.fillStyle = '#ffffff'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(measurement.stats.headline, centerX, centerY - lineHeight / 2)
      ctx.fillText(measurement.stats.detail, centerX, centerY + lineHeight / 2)
      ctx.restore()
    })
  }
}
